import * as React from 'react';
import { VtxColumnProps } from '@vertexinc/vtx-ui-react-component-library';
import {
  capitalizeFirstLetterEachWord,
  CertificateSettingsContext,
  DateFormatEnum,
  DateOptions,
  formatDate,
  formatHumanFriendlyDate,
  settingCertificateCustomField,
  StatusIndicator,
} from '@vcd-ecm-ui/Common';
import {
  ColumnIcon,
  ColumnLink,
  FilterDropdownProps,
  FilterTypeEnum,
  GetActiveVertexColumns,
  SaveFiltersSorterEnum,
  SearchFilter,
  SelectFilter,
  ShowBreadCrumbsEnum,
  TableSettings,
} from '@vcd-ecm-ui/GenericGrid';
import GridRowData from './GridRowData';

// Used by Grid - Display Dates in Local Date
const dateFormat: DateOptions = { dateFormat: DateFormatEnum.MDY, dateSeperator: '/', utcDate: false };

// Used by Date Controls in Filters
const dateFormatString = 'MM/DD/YYYY';

const columns: VtxColumnProps<GridRowData>[] = [
  {
    title: 'Exemp. ID',
    dataIndex: 'certificateId',
    fixed: 'left',
    width: 135,
    sorter: true,
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Number} minimum={0} maximum={2147483647} title="Exemp. ID" dataIndex="certificateId" props={props} />,
  },
  {
    title: 'Reference ID',
    dataIndex: 'ecwCertificateId',
    fixed: 'left',
    width: 150,
    sorter: true,
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Number} minimum={0} maximum={2147483647} title="Reference ID" dataIndex="ecwCertificateId" props={props} />,
  },
  {
    title: 'Create Date',
    dataIndex: 'created',
    fixed: 'left',
    render: (value) => formatHumanFriendlyDate(value),
    // Render the Date Filter using a Timestamp
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Date} title="Create Date" isTimestamp dataIndex="created" props={props} />,
    width: 145,
    sorter: true,
  },
  {
    title: 'Priority',
    dataIndex: 'priority',
    sorter: true,
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Priority" dataIndex="priority" props={props} />,
    className: 'reportColumnStyle',
  },
  {
    title: 'Customer Code',
    // title: <InputHeader title="Customer Code" dataIndex="customerCode" />,
    dataIndex: 'customerCode',
    sorter: true,
    ellipsis: true,
    render: (value, row: GridRowData, index: number) => (
      <ColumnLink id="customerCodeLink" linkText={value} linkAddress={`/#/customer/view/${row.rowData.customerUuid}/1`} />
    ),
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Customer Code" dataIndex="customerCode" props={props} />,
    className: 'reportColumnStyle',
  },
  {
    title: 'Customer Name',
    dataIndex: 'customerName',
    sorter: true,
    ellipsis: true,
    render: (value, row: GridRowData, index: number) => (row.rowData.isParentInd
      ? <ColumnIcon id="customerCodeLink" icon="apartment" iconPlacement="right" value={value} />
      : value
    ),
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Customer Name" dataIndex="customerName" props={props} />,
    className: 'reportColumnStyle',
  },
  {
    title: 'Status',
    dataIndex: 'status',
    sorter: true,
    filters: [
      {
        text: 'Approved',
        value: 'APPROVED',
      },
      {
        text: 'Draft',
        value: 'DRAFT',
      },
      {
        text: 'Pending',
        value: 'PENDING',
      },
      {
        text: 'Rejected',
        value: 'REJECTED',
      },
    ],
    width: 100,
    // eslint-disable-next-line react/destructuring-assignment
    render: (value, row: GridRowData, index: number) => <StatusIndicator status={value.toString()} />,
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SelectFilter multiple={false} title="Status" dataIndex="status" props={props} />,
  },
  {
    title: 'Jurisdiction(s)',
    dataIndex: 'jurisdiction',
    sorter: true,
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SelectFilter multiple title="Jurisdiction(s)" dataIndex="jurisdiction" props={props} />,
    className: 'reportColumnStyle',
  },
  {
    title: 'Reason',
    dataIndex: 'reason',
    sorter: true,
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Reason" dataIndex="reason" props={props} />,
    className: 'reportColumnStyle',
  },
  {
    title: 'Scope',
    dataIndex: 'certificateScope',
    sorter: true,
    filters: [
      {
        text: 'Blanket',
        value: 'BLANKET',
      },
      {
        text: 'Single Use',
        value: 'SINGLE USE',
      },
      {
        text: 'Limited',
        value: 'LIMITED',
      },
    ],
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SelectFilter multiple title="Scope" dataIndex="certificateScope" props={props} />,
    className: 'reportColumnStyle',
  },
  {
    title: 'Source',
    dataIndex: 'source',
    sorter: true,
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Source" dataIndex="source" props={props} />,
    className: 'reportColumnStyle',
  },
  {
    title: 'Start Date',
    dataIndex: 'effDate',
    render: (value) => formatDate(value, dateFormat),
    sorter: true,
    width: 135,
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Date} dateFormat={dateFormatString} title="Start Date" dataIndex="effDate" props={props} />,
  },
  {
    title: 'Expiration Date',
    dataIndex: 'expirationDate',
    render: (value) => formatDate(value, dateFormat),
    sorter: true,
    width: 170,
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Date} dateFormat={dateFormatString} title="Expiration Date" dataIndex="expirationDate" props={props} />,
  },
  {
    title: 'End Date',
    dataIndex: 'endDate',
    render: (value) => formatDate(value, dateFormat),
    sorter: true,
    width: 130,
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Date} dateFormat={dateFormatString} title="End Date" dataIndex="endDate" props={props} />,
  },
  {
    title: 'Owner',
    dataIndex: 'owner',
    sorter: true,
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Owner" dataIndex="owner" props={props} />,
    className: 'reportColumnStyle',
  },
  {
    title: 'Seller',
    dataIndex: 'sellerCode',
    sorter: true,
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Seller" dataIndex="sellerCode" props={props} />,
    className: 'reportColumnStyle',
  },
  {
    title: 'Type',
    dataIndex: 'certificateType',
    sorter: true,
    filters: [
      {
        text: 'Exemption Certificate',
        value: 'Exemption Certificate',
      },
    ],
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SelectFilter multiple title="Type" dataIndex="certificateType" props={props} />,
    className: 'reportColumnStyle',
  },
  {
    title: 'Parent',
    dataIndex: 'isParentInd',
    render: (value, row: GridRowData, index: number) => capitalizeFirstLetterEachWord(value.toString()),
    sorter: true,
    filters: [
      {
        text: 'True',
        value: true,
      },
      {
        text: 'False',
        value: false,
      },
    ],
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SelectFilter multiple={false} title="Parent" filterType={FilterTypeEnum.Boolean} dataIndex="isParentInd" props={props} />,
    className: 'reportColumnStyle',
  },
  {
    title: 'Custom Field 1',
    dataIndex: 'customField1',
    sorter: true,
    className: 'reportColumnStyle',
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Custom Field 1" dataIndex="customField1" props={props} />,
  },
  {
    title: 'Custom Field 2',
    dataIndex: 'customField2',
    sorter: true,
    className: 'reportColumnStyle',
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Custom Field 2" dataIndex="customField2" props={props} />,
  },
  {
    title: 'Custom Field 3',
    dataIndex: 'customField3',
    sorter: true,
    className: 'reportColumnStyle',
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Custom Field 3" dataIndex="customField3" props={props} />,
  },
  {
    title: 'Custom Field 4',
    dataIndex: 'customField4',
    sorter: true,
    className: 'reportColumnStyle',
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Custom Field 4" dataIndex="customField4" props={props} />,
  },
  {
    title: 'Custom Field 5',
    dataIndex: 'customField5',
    sorter: true,
    className: 'reportColumnStyle',
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Custom Field 5" dataIndex="customField5" props={props} />,
  },
  {
    title: 'Custom Field 6',
    dataIndex: 'customField6',
    sorter: true,
    className: 'reportColumnStyle',
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Custom Field 6" dataIndex="customField6" props={props} />,
  },
  {
    title: 'Custom Field 7',
    dataIndex: 'customField7',
    sorter: true,
    className: 'reportColumnStyle',
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Custom Field 7" dataIndex="customField7" props={props} />,
  },
  {
    title: 'Custom Field 8',
    dataIndex: 'customField8',
    sorter: true,
    className: 'reportColumnStyle',
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Custom Field 8" dataIndex="customField8" props={props} />,
  },
  {
    title: 'Custom Field 9',
    dataIndex: 'customField9',
    sorter: true,
    className: 'reportColumnStyle',
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Custom Field 9" dataIndex="customField9" props={props} />,
  },
  {
    title: 'Custom Field 10',
    dataIndex: 'customField10',
    sorter: true,
    className: 'reportColumnStyle',
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Custom Field 10" dataIndex="customField10" props={props} />,
  },
  {
    title: 'Action',
    dataIndex: 'action',
    fixed: 'right',
    width: 100,
  },
];

// Additional Table Settings - Will add the the Table Settings extracted from Columns
export const additionalTableSettings: TableSettings = {
  version: 'v1',
  hideDisabledColumns: false,
  saveFiltersSorter: SaveFiltersSorterEnum.SESSION_STORAGE,
  showBreadcrumbs: ShowBreadCrumbsEnum.ALL,
  columns: [
    {
      dataIndex: 'action',
      canChangePin: false,
      canMove: false,
      canChangeVisibilty: false,
    },
  ],
};

const getDefaultColumns = (certificateSettingsContext: CertificateSettingsContext): VtxColumnProps<GridRowData>[] => GetActiveVertexColumns(
  columns,
  certificateSettingsContext,
  settingCertificateCustomField,
  'Custom Field',
);

export default getDefaultColumns;
